import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { formatMoney, PROMOTION_KINDS, type CurrencyCode } from '@tengesa/shared';
import { api } from '../../lib/api';
import { addDays, todayLocal } from '../../lib/dates';
import type { Category } from '../../lib/types';
import { useSession } from '../../app/session';
import { Badge, Button, Checkbox, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, Switch, TextInput, fieldErrors, useToast } from '../../ui';
import { MoneyInput, ProductPicker } from '../../ui/inputs';

type Kind = keyof typeof PROMOTION_KINDS;
interface Promotion {
  id: string;
  name: string;
  kind: Kind;
  percentBp: number | null;
  fixedPriceCents: number | null;
  buyQty: number | null;
  getQty: number | null;
  startsOn: string;
  endsOn: string;
  branchIds: string[];
  productIds: string[];
  categoryIds: string[];
  productNames: string[];
  categoryNames: string[];
  isActive: boolean;
  state: 'running' | 'upcoming' | 'ended' | 'paused';
}

const STATE = {
  running: { label: 'Running', tone: 'ok' as const },
  upcoming: { label: 'Starts later', tone: 'gold' as const },
  ended: { label: 'Ended', tone: 'neutral' as const },
  paused: { label: 'Paused', tone: 'warn' as const },
};

export function PromotionsPage() {
  const { me } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const [editing, setEditing] = useState<Promotion | 'new' | null>(null);
  const q = useQuery({ queryKey: ['promotions'], queryFn: () => api.get<Promotion[]>('/api/promotions') });
  const branchName = (id: string) => me.branches.find((b) => b.id === id)?.name ?? 'another branch';
  const describe = (p: Promotion) =>
    p.kind === 'percent_off' ? `${(p.percentBp ?? 0) / 100}% off` : p.kind === 'fixed_price' ? `Now ${formatMoney(p.fixedPriceCents ?? 0, cur)} each` : `Buy ${p.buyQty}, get ${p.getQty} free`;
  return (
    <>
      <PageHeader
        title="Promotions"
        description="Price offers the till applies by itself between their start and end dates. A customer gets the single best offer on each item."
        actions={<Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>New promotion</Button>}
      />
      <Panel>
        {q.isLoading && <Spinner />}
        {q.error && <div className="p-4"><ErrorNotice error={q.error} /></div>}
        {q.data?.length === 0 && <EmptyState title="No promotions yet" action={<Button variant="primary" onClick={() => setEditing('new')}>New promotion</Button>} />}
        {!!q.data?.length && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2.5 font-medium">Promotion</th>
                  <th className="px-3 py-2.5 font-medium">Applies to</th>
                  <th className="px-3 py-2.5 font-medium">Dates</th>
                  <th className="px-5 py-2.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {q.data.map((p) => (
                  <tr key={p.id} className="hover:bg-ledger-50/60">
                    <td className="px-5 py-3">
                      <button className="text-left font-medium hover:underline" onClick={() => setEditing(p)}>{p.name}</button>
                      <span className="block text-sm text-muted">{describe(p)}</span>
                    </td>
                    <td className="px-3 py-3 text-sm">
                      {[...p.categoryNames.map((c) => `All ${c}`), ...p.productNames].slice(0, 3).join(', ')}
                      {p.productNames.length + p.categoryNames.length > 3 && ` and ${p.productNames.length + p.categoryNames.length - 3} more`}
                      <span className="block text-xs text-muted">{p.branchIds.length ? p.branchIds.map(branchName).join(', ') : 'All branches'}</span>
                    </td>
                    <td className="px-3 py-3 text-sm num">{p.startsOn === p.endsOn ? p.startsOn : `${p.startsOn} to ${p.endsOn}`}</td>
                    <td className="px-5 py-3"><Badge tone={STATE[p.state].tone}>{STATE[p.state].label}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {editing && <PromotionDialog promo={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function PromotionDialog({ promo, onClose }: { promo: Promotion | null; onClose: () => void }) {
  const { me } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const qc = useQueryClient();
  const toast = useToast();
  const cats = useQuery({ queryKey: ['categories'], queryFn: () => api.get<Category[]>('/api/categories') });
  const [v, setV] = useState({
    name: promo?.name ?? '',
    kind: promo?.kind ?? ('percent_off' as Kind),
    percent: promo?.percentBp ? String(promo.percentBp / 100) : '',
    fixedPriceCents: promo?.fixedPriceCents ?? null,
    buyQty: promo?.buyQty ? String(promo.buyQty) : '2',
    getQty: promo?.getQty ? String(promo.getQty) : '1',
    startsOn: promo?.startsOn ?? todayLocal(),
    endsOn: promo?.endsOn ?? addDays(todayLocal(), 6),
    branchIds: promo?.branchIds ?? [],
    products: (promo?.productIds ?? []).map((id, i) => ({ id, name: promo!.productNames[i] ?? '' })),
    categoryIds: promo?.categoryIds ?? [],
    isActive: promo?.isActive ?? true,
  });
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((p) => ({ ...p, [k]: val }));
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const m = useMutation({
    mutationFn: () => {
      const body = {
        name: v.name,
        kind: v.kind,
        percentBp: v.kind === 'percent_off' ? Math.round(Number(v.percent) * 100) || null : null,
        fixedPriceCents: v.kind === 'fixed_price' ? v.fixedPriceCents : null,
        buyQty: v.kind === 'buy_x_get_y' ? Number(v.buyQty) || null : null,
        getQty: v.kind === 'buy_x_get_y' ? Number(v.getQty) || null : null,
        startsOn: v.startsOn,
        endsOn: v.endsOn,
        branchIds: v.branchIds,
        productIds: v.products.map((p) => p.id),
        categoryIds: v.categoryIds,
        isActive: v.isActive,
      };
      return promo ? api.put(`/api/promotions/${promo.id}`, body) : api.post('/api/promotions', body);
    },
    onSuccess: () => {
      toast(promo ? 'Promotion saved' : 'Promotion created');
      for (const k of ['promotions', 'active-promotions']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={promo ? `Edit ${promo.name}` : 'New promotion'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={m.isPending} onClick={() => m.mutate()}>{promo ? 'Save promotion' : 'Create promotion'}</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" htmlFor="pr-name" error={f.name} hint="Printed on the receipt next to the discount">
          <TextInput id="pr-name" value={v.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Month-end braai special" />
        </Field>
        <Field label="Kind of offer" htmlFor="pr-kind">
          <Select id="pr-kind" value={v.kind} onChange={(e) => set('kind', e.target.value as Kind)}>
            {Object.entries(PROMOTION_KINDS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </Select>
        </Field>
        {v.kind === 'percent_off' && (
          <Field label="Percentage off" htmlFor="pr-pct" error={f.percentBp}>
            <TextInput id="pr-pct" inputMode="decimal" className="num" value={v.percent} onChange={(e) => set('percent', e.target.value)} placeholder="10" />
          </Field>
        )}
        {v.kind === 'fixed_price' && (
          <Field label="Special price each" htmlFor="pr-fixed" error={f.fixedPriceCents}>
            <MoneyInput id="pr-fixed" currency={cur} value={v.fixedPriceCents} onChange={(c) => set('fixedPriceCents', c)} />
          </Field>
        )}
        {v.kind === 'buy_x_get_y' && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer buys" htmlFor="pr-buy" error={f.buyQty}>
              <TextInput id="pr-buy" inputMode="numeric" className="num" value={v.buyQty} onChange={(e) => set('buyQty', e.target.value.replace(/\D/g, ''))} />
            </Field>
            <Field label="Of which free" htmlFor="pr-get">
              <TextInput id="pr-get" inputMode="numeric" className="num" value={v.getQty} onChange={(e) => set('getQty', e.target.value.replace(/\D/g, ''))} />
            </Field>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Starts" htmlFor="pr-start" error={f.startsOn}>
            <TextInput id="pr-start" type="date" value={v.startsOn} onChange={(e) => set('startsOn', e.target.value)} />
          </Field>
          <Field label="Ends (last day)" htmlFor="pr-end" error={f.endsOn}>
            <TextInput id="pr-end" type="date" value={v.endsOn} onChange={(e) => set('endsOn', e.target.value)} />
          </Field>
        </div>
      </div>
      {v.kind === 'buy_x_get_y' && Number(v.getQty) >= Number(v.buyQty) && <p className="mt-2 text-sm text-warn">The free quantity should be less than the quantity bought.</p>}

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <div>
          <ProductPicker label="Products" placeholder="Add a product" onPick={(p) => !v.products.some((x) => x.id === p.id) && set('products', [...v.products, { id: p.id, name: p.name }])} />
          {f.productIds && <p className="mt-1 text-sm text-danger">{f.productIds}</p>}
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {v.products.map((p) => (
              <li key={p.id} className="inline-flex items-center gap-1 rounded-full bg-ledger-50 py-1 pl-3 pr-1 text-sm">
                {p.name}
                <button className="rounded-full p-0.5 text-muted hover:bg-ink/10" aria-label={`Remove ${p.name}`} onClick={() => set('products', v.products.filter((x) => x.id !== p.id))}>
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
          {!!cats.data?.length && (
            <>
              <p className="mt-4 mb-2 text-sm font-medium">Or whole categories</p>
              <div className="grid max-h-44 gap-2 overflow-y-auto">
                {cats.data.map((c) => <Checkbox key={c.id} label={c.name} checked={v.categoryIds.includes(c.id)} onChange={() => set('categoryIds', toggle(v.categoryIds, c.id))} />)}
              </div>
            </>
          )}
        </div>
        <div>
          <p className="mb-2 text-sm font-medium">Branches</p>
          <div className="grid gap-2">
            <Checkbox label="All branches" checked={v.branchIds.length === 0} onChange={() => set('branchIds', [])} />
            {me.branches.length > 1 && me.branches.map((b) => <Checkbox key={b.id} label={b.name} checked={v.branchIds.includes(b.id)} onChange={() => set('branchIds', toggle(v.branchIds, b.id))} />)}
          </div>
          <div className="mt-5 flex items-center justify-between gap-3 rounded-[8px] border border-line px-4 py-3">
            <div>
              <p className="font-medium">Active</p>
              <p className="text-sm text-muted">Switch off to pause it without deleting.</p>
            </div>
            <Switch checked={v.isActive} onChange={(x) => set('isActive', x)} label="Active" />
          </div>
        </div>
      </div>
    </Dialog>
  );
}
